import { metadataSubscriptionAssignmentKeyPrefix } from '@/constants/subscriptions'
import {
  ICustomerSubscription,
  ISubscriptionPlanOption,
  ISubscriptionPlanWithPrices,
  SubscriptionPriceInterval,
  SubscriptionType,
} from '@/types/firebase'
import { stripe } from '@/config/stripe'

const MODE = process.env.NEXT_PUBLIC_MODE

export function generateMetadataSubscriptionAssignmentKey(id: string): string {
  return `${metadataSubscriptionAssignmentKeyPrefix}_${id}`
}

export function extractStudentsIdsAssignedToParentSubscription(
  obj: Record<string, string>,
): string[] {
  const targetValues: string[] = []

  for (const key in obj) {
    if (key.includes(metadataSubscriptionAssignmentKeyPrefix)) {
      targetValues.push(obj[key])
    }
  }

  return targetValues
}

export const getCurrentPlanInterval = (selectedPlan: ICustomerSubscription) => {
  if (selectedPlan && selectedPlan.prices.length > 0) {
    if (selectedPlan.prices[0].interval === SubscriptionPriceInterval.Year) {
      return 2
    } else if (
      selectedPlan.prices[0].interval === SubscriptionPriceInterval.Month
    ) {
      return 1
    }
  }

  return 1
}

export async function createStripeCustomer(email: string) {
  return await stripe.customers.create({ email })
}

export async function addTrialSubscription(customerId: string) {
  return await stripe.subscriptions.create({
    customer: customerId,
    items: [
      {
        price: process.env.NEXT_PUBLIC_TRIAL_BASE_PRICING_KEY,
      },
    ],
    trial_end: Math.floor((Date.now() + 7 * 24 * 60 * 60 * 1000) / 1000),
  })
}

export function filterSubscriptionPlans(
  plans: ISubscriptionPlanWithPrices[],
  billingFrequency?: SubscriptionPriceInterval,
) {
  // A product can have multiple prices (e.g. Boost+ has monthly + weekly).
  // Filter to plans with ANY price matching billingFrequency, then promote
  // that price to prices[0] so downstream code reading prices[0] renders
  // the right amount/interval.
  const matchesMode = (item) =>
    MODE === 'DEV' ? item?.metadata?.test : !item?.metadata?.test
  const isFreePlan = (p) => p.role === SubscriptionType.Free
  // New plan structure sells Free + Unlimited only. Boost / Boost+ /
  // ExamPack plan docs stay in Firestore for existing subscribers until the
  // post-release migration (owned by Pavlo), but must not render as buyable
  // tiles.
  const isSoldRole = (p) =>
    p.role === SubscriptionType.Free || p.role === SubscriptionType.Unlimited

  let filtered = plans
    .filter((item) => {
      // check if the item matches the mode and has prices
      if (!matchesMode(item) || !item.prices?.length) {
        return false
      }
      if (!isSoldRole(item)) {
        return false
      }
      // if billing frequency is specified, return only plans that have a price matching that frequency
      if (billingFrequency) {
        return item.prices.some((p) => p.interval === billingFrequency)
      }
      // if no billing frequency is specified, return all plans that match the mode
      return item
    })
    .map((plan) => {
      const matchingPrice = plan.prices.find(
        (p) => p.interval === billingFrequency,
      )
      if (matchingPrice && plan.prices[0]?.id !== matchingPrice.id) {
        return {
          ...plan,
          prices: [
            matchingPrice,
            ...plan.prices.filter((p) => p.id !== matchingPrice.id),
          ],
        }
      }
      return plan
    })

  // Legacy Firestore data has separate Free products per interval (Free
  // Monthly £0, Free Annual £0). On a frequency tab where no Free plan
  // matches, fall back to the lowest-position Free plan so the Free card
  // is always rendered.
  if (!filtered.some(isFreePlan)) {
    const freeFallback = plans
      .filter((p) => matchesMode(p) && isFreePlan(p) && p.prices?.length)
      .sort((a, b) => (a.position ?? Infinity) - (b.position ?? Infinity))[0]
    if (freeFallback) filtered = [...filtered, freeFallback]
  }

  return filtered
}

export function transformPlanToOption(
  plan: ISubscriptionPlanWithPrices,
): ISubscriptionPlanOption {
  return {
    priceId: plan.prices[0].id,
    planId: plan.id,
    name: `${plan.name} - ${
      plan.prices[0].interval === 'year'
        ? 'Annual'
        : plan.prices[0].interval === 'month'
        ? 'Monthly'
        : 'Weekly'
    } (${(plan.prices[0].unit_amount / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: plan.prices[0].currency || 'GBP',
    })}/${
      plan.prices[0].interval === 'year'
        ? 'year'
        : plan.prices[0].interval === 'month'
        ? 'month'
        : 'week'
    })`,
  }
}
